"use client";
import { useMutation } from "@apollo/client/react";
import { addBreadcrumb, captureMessage, setTag } from "@sentry/nextjs";
import { logger } from "@portal/app/_common/lib/logger";
import { EVERFLOW_LOADED_EVENT_NAME, EVERFLOW_OFFER_ID } from "@portal/app/_common/types/everflow";
import { getSafeUrlParam } from "@portal/app/_common/lib/urlParamUtils";
import {
  detectInAppBrowser,
  getUserAgent,
  parseUaVersions,
} from "@portal/app/_common/lib/browserDetection";
import EverflowSDK from "@everflow/everflow-sdk";
import { parseEverflowAffiliateParamsFromSearch } from "@portal/app/_common/lib/everflowAffiliateParams";
import { COOKIE_KEYS, setCookie } from "@portal/app/_common/lib/headers";
import { useEffect, useRef } from "react";
import { gql } from "graphql-tag";
import { useEverflow } from "@portal/app/_components/hooks/useEverflow";
import { getEnvironmentName, isProduction } from "@portal/app/_common/lib/edgeUtils";
import { PersistEverflowTransactionIdDocument } from "./generated";

void gql`
  mutation persistEverflowTransactionId {
    persistEverflowTransactionId {
      status
      transactionId
    }
  }
`;

const PERSISTED_TID_SESSION_STORAGE_KEY = "dreambound:everflow:persistedTid";

/**
 * Matches the Everflow SDK's own 30-day TID cookie window so the affiliate
 * params survive as long as the transaction ID they belong to.
 */
const AFFILIATE_PARAMS_COOKIE_EXPIRES_DAYS = 30;

/**
 * Captures Everflow affiliate params (oid, affid, sub1-8) from the landing
 * URL into a first-party cookie. The query string is gone after the first
 * navigation, but leads are created pages later (marketplace intake, program
 * flow, clickout), so lead-creation surfaces read this cookie to stamp
 * enrollmentItem.metadata.tracking. Last-touch: a new landing WITH params
 * replaces the whole set; a landing without params leaves the capture intact.
 * Runs independently of SDK init — this is our own attribution capture, not
 * an Everflow handshake.
 */
function persistAffiliateParamsFromUrl(): void {
  const params = parseEverflowAffiliateParamsFromSearch(window.location.search);
  if (!params) return;
  setCookie(COOKIE_KEYS.EVERFLOW_AFFILIATE_PARAMS, JSON.stringify(params), {
    expires: AFFILIATE_PARAMS_COOKIE_EXPIRES_DAYS,
  });
}

function readPersistedTid(): string | null {
  try {
    return window.sessionStorage.getItem(PERSISTED_TID_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersistedTid(tid: string): void {
  try {
    window.sessionStorage.setItem(PERSISTED_TID_SESSION_STORAGE_KEY, tid);
  } catch {
    // sessionStorage can throw in Safari private mode / some in-app browsers.
    // The in-memory ref still prevents double-fires within this page load.
  }
}

if (!process.env.EVERFLOW_TRACKING_DOMAIN && isProduction()) {
  throw new Error("EVERFLOW_TRACKING_DOMAIN is not set");
}

const trackingDomain = process.env.EVERFLOW_TRACKING_DOMAIN;
const isEverflowDisabled = trackingDomain?.includes("intentionally-fail");

type FailedStep = "configure" | "click_threw" | "click_timeout" | "transactionId_empty";

/** Fraction of never-fired init cases to log (keeps volume bounded on fast navigations). */
const NEVER_FIRED_SAMPLE_RATE = 0.05;

/**
 * How long we wait before emitting the "capture timeout" warning if the SDK
 * still hasn't resolved. This is the upper bound on our tolerance for a slow
 * SDK response — form funnels that rely on the `ef_tid_c_o_<offer>` cookie
 * being present at submit won't recover past this window.
 */
const CAPTURE_TIMEOUT_MS = 30_000;

/**
 * Tracks whether `run()` has actually been called at least once so we can
 * detect "scheduled but never fired" cases on cleanup — users who navigate
 * away before requestIdleCallback / setTimeout fires.
 */
let runStarted = false;

/**
 * Transaction ID the SDK resolved with on success. Read by the capture-timeout
 * warning below to distinguish "SDK still hasn't resolved" from "SDK resolved
 * fine" at the 30s mark.
 */
let resolvedTid: string | undefined;

/**
 * The Everflow SDK's click() promise never resolves when the tracking domain
 * responds without a transaction_id (common in in-app browsers that restrict
 * cross-origin cookies). This timeout prevents the await from hanging forever
 * so the retry loop can fire.
 */
const SDK_CLICK_TIMEOUT_MS = 8_000;

/**
 * In in-app browsers, EverflowSDK.click() consistently times out because
 * cross-origin cookie restrictions prevent the tracking domain from returning
 * a transaction_id. Cap retries to fail fast instead of burning 20 attempts.
 */
const IN_APP_BROWSER_MAX_RETRIES = 3;

/** Wraps a promise with a timeout to prevent indefinite hanging. */
function withTimeout<T>(promise: Promise<T>, ms: number, label = "Operation"): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (val) => {
        clearTimeout(timer);
        resolve(val);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Returns Network Information API data when available (experimental API, not in all browsers). */
function getNetworkInfo(): Record<string, unknown> | undefined {
  const conn = (
    navigator as unknown as {
      connection?: { effectiveType?: string; downlink?: number; rtt?: number };
    }
  ).connection;
  if (!conn) return undefined;
  return {
    effectiveType: conn.effectiveType,
    downlink: conn.downlink,
    rtt: conn.rtt,
  };
}

/**
 * Cloudflare worker proxy in front of the Everflow tracking domain. The
 * primary custom domain (`e.dreambound.com`, an Everflow-managed endpoint) is
 * unreachable at the network level for a meaningful share of mobile clients —
 * plain Mobile Safari / Chrome Mobile as well as Facebook and Instagram in-app
 * browsers — while this first-party proxy answers for the same devices. It is
 * the fallback tracking domain for every init attempt after the first failure
 * (see everflowInit) and is probed on retry exhaustion so the failure event
 * records whether even the proxy was unreachable.
 */
const proxyDomain = process.env.EVERFLOW_PROXY_DOMAIN;

/**
 * Independent reachability probe, run on retry exhaustion for both domains.
 * Bypasses the Everflow SDK entirely so we get a clean signal about whether
 * the domain is blocked at the network/browser level.
 */
async function probeDomain(domain: string | undefined): Promise<Record<string, unknown>> {
  if (!domain) return { error: "no domain configured" };
  try {
    const start = performance.now();
    const res = await fetch(`${domain}/sdk/click?async=json&probe=1`, {
      method: "GET",
      credentials: "include",
    });
    return {
      reachable: true,
      status: res.status,
      latencyMs: Math.round(performance.now() - start),
    };
  } catch (err) {
    return { reachable: false, error: errorMessage(err) };
  }
}

/**
 * How long requestIdleCallback waits before forcing execution. Keeps init
 * from being deferred indefinitely on busy pages.
 */
const EVERFLOW_IDLE_TIMEOUT_MS = 4_000;

/**
 * Short delay after window.load before running init when requestIdleCallback
 * is unavailable. Gives the browser a moment to settle after load fires.
 */
const EVERFLOW_LOAD_FALLBACK_DELAY_MS = 1_500;

/** Module-level guard so only the first mount schedules init (Strict Mode fires useEffect twice). */
let initScheduled = false;

/**
 * Module-level guard for the 30s capture-timeout warning. Scheduled once per
 * page load alongside init; emits a Sentry warning if the SDK still hasn't
 * resolved (no `resolvedTid`) after CAPTURE_TIMEOUT_MS. This used to live in
 * FormFunnelTrackingProvider but was funnel-scoped and duplicated the
 * cookie-jar inspection we already do here. Module-level is fine because
 * EverflowScript mounts once in the root layout.
 */
let captureTimeoutScheduled = false;

function scheduleCaptureTimeoutWarning() {
  if (captureTimeoutScheduled) return;
  captureTimeoutScheduled = true;
  setTimeout(() => {
    if (resolvedTid) return;
    const inAppBrowser = detectInAppBrowser();
    const ua = getUserAgent();
    const versions = parseUaVersions(ua);
    captureMessage("Everflow transaction ID capture timeout", {
      level: "warning",
      tags: {
        feature: "everflow",
        "everflow.outcome": "capture_timeout",
        ...(inAppBrowser && { inAppBrowser }),
        ...(versions.fbAppVersion && { fb_app_version: versions.fbAppVersion }),
        ...(versions.webviewChromeVersion && {
          webview_chrome_version: versions.webviewChromeVersion,
        }),
        ...(versions.iosWebKitVersion && { ios_webkit_version: versions.iosWebKitVersion }),
      },
      extra: {
        timeoutMs: CAPTURE_TIMEOUT_MS,
        runStarted,
        retryCount,
        lastFailedStep,
        userAgent: ua,
        sdkHasTransactionId: !!EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID),
        cookiesEnabled: typeof navigator !== "undefined" ? navigator.cookieEnabled : undefined,
        pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
      },
    });
  }, CAPTURE_TIMEOUT_MS);
}

/**
 * Schedules everflowInit() for browser idle time instead of running it
 * immediately during React hydration. Uses requestIdleCallback where
 * available, otherwise waits for window.load + a short delay.
 *
 * Returns a cleanup function that cancels any pending scheduling.
 */
function scheduleEverflowInit(): () => void {
  if (initScheduled) return () => {};
  initScheduled = true;

  let cancelled = false;
  let idleHandle: number | undefined;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  const run = () => {
    if (cancelled) return;
    runStarted = true;
    addBreadcrumb({
      category: "everflow",
      message: "Init starting",
      level: "info",
    });
    resetRetryCount();
    everflowInit();
  };

  if (typeof window.requestIdleCallback === "function") {
    addBreadcrumb({
      category: "everflow",
      message: "Init scheduled via requestIdleCallback",
      level: "info",
      data: { timeoutMs: EVERFLOW_IDLE_TIMEOUT_MS },
    });
    idleHandle = window.requestIdleCallback(run, { timeout: EVERFLOW_IDLE_TIMEOUT_MS });
  } else if (document.readyState === "complete") {
    addBreadcrumb({
      category: "everflow",
      message: "Init scheduled via setTimeout (page already loaded)",
      level: "info",
      data: { delayMs: EVERFLOW_LOAD_FALLBACK_DELAY_MS },
    });
    timeoutHandle = setTimeout(run, EVERFLOW_LOAD_FALLBACK_DELAY_MS);
  } else {
    const onLoad = () => {
      addBreadcrumb({
        category: "everflow",
        message: "Init scheduled via load event + setTimeout fallback",
        level: "info",
        data: { delayMs: EVERFLOW_LOAD_FALLBACK_DELAY_MS },
      });
      timeoutHandle = setTimeout(run, EVERFLOW_LOAD_FALLBACK_DELAY_MS);
    };
    window.addEventListener("load", onLoad, { once: true });

    return () => {
      cancelled = true;
      initScheduled = false;
      window.removeEventListener("load", onLoad);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      maybeCaptureNeverFired("load_event_pending");
    };
  }

  return () => {
    cancelled = true;
    initScheduled = false;
    if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    maybeCaptureNeverFired(idleHandle !== undefined ? "idle_pending" : "timeout_pending");
  };
}

/**
 * Captures (at a low sample rate) the case where init was scheduled but
 * never actually ran because the component unmounted / page navigated first.
 * Complements the failure-event capture — those only fire once init has
 * actually attempted and exhausted retries. This covers the "SDK never got
 * a chance to try" population that currently produces no telemetry.
 */
function maybeCaptureNeverFired(pendingKind: "idle_pending" | "timeout_pending" | "load_event_pending") {
  if (runStarted) return;
  if (Math.random() >= NEVER_FIRED_SAMPLE_RATE) return;
  const inAppBrowser = detectInAppBrowser();
  const ua = getUserAgent();
  const versions = parseUaVersions(ua);
  captureMessage("Everflow init scheduled but never fired before unmount", {
    level: "warning",
    tags: {
      feature: "everflow",
      "everflow.outcome": "never_fired",
      "everflow.pendingKind": pendingKind,
      ...(inAppBrowser && { inAppBrowser }),
      ...(versions.fbAppVersion && { fb_app_version: versions.fbAppVersion }),
      ...(versions.webviewChromeVersion && {
        webview_chrome_version: versions.webviewChromeVersion,
      }),
      ...(versions.iosWebKitVersion && { ios_webkit_version: versions.iosWebKitVersion }),
    },
    extra: { userAgent: ua, pageUrl: window.location.href },
  });
}

export const EverflowScript = () => {
  // Ref tracks whether this instance already scheduled init (guards against
  // the Strict Mode double-mount without leaking across navigations).
  const scheduledRef = useRef(false);

  // Once the SDK resolves a transaction ID, immediately persist it onto the
  // session customer. Firing here (not at form-funnel submit) means we
  // capture the TID regardless of whether the user ever submits — including
  // sessions that bounce before the cookie would have mattered. Guards
  // against redundant fires via an in-memory ref + sessionStorage (across
  // SPA navigations) and a server-side ALREADY_PERSISTED no-op.
  const { everflowTransactionId } = useEverflow();
  const persistedForTidRef = useRef<string | null>(null);
  const [persistEverflowTransactionId] = useMutation(PersistEverflowTransactionIdDocument);

  useEffect(() => {
    // Before the SDK-gated early returns: affiliate-param capture is our own
    // first-party attribution and shouldn't depend on SDK configuration.
    // Idempotent, so the Strict Mode double-mount is harmless.
    persistAffiliateParamsFromUrl();

    if (!trackingDomain || isEverflowDisabled) return;
    if (scheduledRef.current) return;
    scheduledRef.current = true;

    scheduleCaptureTimeoutWarning();
    return scheduleEverflowInit();
  }, []);

  useEffect(() => {
    if (!everflowTransactionId) return;
    if (persistedForTidRef.current === everflowTransactionId) return;
    if (readPersistedTid() === everflowTransactionId) {
      persistedForTidRef.current = everflowTransactionId;
      return;
    }

    persistedForTidRef.current = everflowTransactionId;
    writePersistedTid(everflowTransactionId);

    persistEverflowTransactionId().catch((error) => {
      logger.error(error, "Failed to persist Everflow transaction ID");
    });
  }, [everflowTransactionId, persistEverflowTransactionId]);

  return null;
};

export async function everflowInit(timeout = initialWaitTimeout) {
  lastFailedStep = undefined;

  // Step 1: Configure the SDK. The first attempt goes to the primary tracking
  // domain; once that has failed, every retry goes through the Cloudflare
  // proxy instead (see `proxyDomain`). Each attempt reconfigures the SDK, so
  // switching is just a different tracking_domain here.
  const usingProxy = !!proxyDomain && retryCount > 0;
  try {
    EverflowSDK.configure({
      tracking_domain: usingProxy ? proxyDomain : trackingDomain,
      organic: {
        offer_id: EVERFLOW_OFFER_ID,
        affiliate_id: 3,
      },
    });
  } catch (error) {
    lastError = error;
    lastFailedStep = "configure";
    logger.error(error);
    waitAndRetryInit(timeout);
    return;
  }

  // Step 2: Register the click
  try {
    // click() options stop at sub5; sub6-8 (FB ad/adset/campaign IDs) ride the
    // free-form `parameters` map instead, which appends entries verbatim with
    // no null-skip — so only include the params actually on the URL.
    const extraSubParams: Record<string, string> = {};
    for (const key of ["sub6", "sub7", "sub8"]) {
      const value = EverflowSDK.urlParameter(key);
      if (value) extraSubParams[key] = value;
    }
    const eid = await withTimeout(
      EverflowSDK.click({
        offer_id: EverflowSDK.urlParameter("oid"),
        affiliate_id: EverflowSDK.urlParameter("affid"),
        sub1: EverflowSDK.urlParameter("sub1"),
        sub2: EverflowSDK.urlParameter("sub2"),
        sub3: EverflowSDK.urlParameter("sub3"),
        sub4: EverflowSDK.urlParameter("sub4"),
        sub5: EverflowSDK.urlParameter("sub5"),
        uid: EverflowSDK.urlParameter("uid"),
        source_id: EverflowSDK.urlParameter("source_id"),
        // Use safe URL param parsing to handle malformed params (e.g., ?_ef_transaction_id without '=')
        // that would otherwise prevent organic fallback — see ENG-5906.
        // Fall back to the SDK's own 30-day cookie so returning visitors bypass the
        // ef_witness guard and the SDK always makes a server call, ensuring Everflow
        // has a click record for the transaction ID.
        transaction_id:
          getSafeUrlParam("_ef_transaction_id") ||
          getSafeUrlParam("ef_transaction_id") ||
          EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID),
        // The SDK captures ScCid from the URL and skips it when absent.
        // Putting it in parameters would serialize a missing value as "null".
        parameters: {
          environment: getEnvironmentName(),
          ...extraSubParams,
        },
      }),
      SDK_CLICK_TIMEOUT_MS,
      "EverflowSDK.click()"
    );
    const everflowTransactionId = eid || EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID);
    if (everflowTransactionId) {
      resolvedTid = everflowTransactionId;
      window.dispatchEvent(
        new CustomEvent(EVERFLOW_LOADED_EVENT_NAME, {
          detail: everflowTransactionId,
        })
      );
      // Rides every later Sentry event from this session at no extra volume,
      // so the proxy rescue rate can be read off events we already emit.
      if (usingProxy) {
        setTag("everflow.resolvedVia", "proxy");
        addBreadcrumb({
          category: "everflow",
          message: "Transaction ID resolved via proxy fallback",
          level: "info",
          data: { retryCount },
        });
      }
    } else {
      lastFailedStep = "transactionId_empty";
      throw new Error("Everflow transaction ID is not set after successful click()");
    }
  } catch (error) {
    lastError = error;
    if (lastFailedStep === undefined) {
      // Distinguish the two click() failure modes so the Sentry breakdown
      // can tell "Everflow never responded within 8s" (timeout) from "SDK
      // itself threw synchronously or rejected the promise" (threw).
      lastFailedStep = errorMessage(error).includes("timed out after")
        ? "click_timeout"
        : "click_threw";
    }
    logger.error(error);
    waitAndRetryInit(timeout);
  }
}

const initialWaitTimeout = 500;
const timeoutIncrement = 500;
const maxRetries = 20;
let retryCount = 0;
let lastError: unknown;
let lastFailedStep: FailedStep | undefined;

export function resetRetryCount() {
  retryCount = 0;
  lastError = undefined;
  lastFailedStep = undefined;
}

const waitAndRetryInit = (timeout: number) =>
  setTimeout(async () => {
    try {
      const inAppBrowser = detectInAppBrowser();
      const effectiveMaxRetries = inAppBrowser ? IN_APP_BROWSER_MAX_RETRIES : maxRetries;

      if (retryCount >= effectiveMaxRetries) {
        const [probeResult, proxyProbeResult] = await Promise.all([
          probeDomain(trackingDomain),
          probeDomain(proxyDomain),
        ]);
        const ua = getUserAgent();
        const versions = parseUaVersions(ua);
        return captureMessage(`Everflow setup failed to load after ${retryCount} retries`, {
          level: "error",
          tags: {
            feature: "everflow",
            "everflow.failedStep": lastFailedStep,
            "everflow.domainReachable": String(probeResult.reachable ?? "unknown"),
            "everflow.proxyReachable": String(proxyProbeResult.reachable ?? "unknown"),
            ...(inAppBrowser && { inAppBrowser }),
            ...(versions.fbAppVersion && { fb_app_version: versions.fbAppVersion }),
            ...(versions.webviewChromeVersion && {
              webview_chrome_version: versions.webviewChromeVersion,
            }),
            ...(versions.iosWebKitVersion && { ios_webkit_version: versions.iosWebKitVersion }),
          },
          extra: {
            lastError: errorMessage(lastError),
            lastErrorStack: lastError instanceof Error ? lastError.stack : undefined,
            inAppBrowser,
            userAgent: ua,
            effectiveMaxRetries,
            retryCount,
            pageUrl: window.location.href,
            sdkScriptLoaded: !!document.querySelector('script[src*="everflow"]'),
            networkInfo: getNetworkInfo(),
            probeResult,
            proxyProbeResult,
          },
        });
      }
      retryCount++;

      addBreadcrumb({
        category: "everflow",
        message: `Retry ${retryCount}/${effectiveMaxRetries}`,
        level: "info",
        data: {
          timeout,
          failedStep: lastFailedStep,
          errorMessage: errorMessage(lastError),
          nextTrackingDomain: proxyDomain ? "proxy" : "primary",
        },
      });

      await everflowInit(timeout + timeoutIncrement);
    } catch (error) {
      logger.error(error);
      waitAndRetryInit(timeout + timeoutIncrement);
    }
  }, timeout);
